'use client'
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useFormspree } from '@/hooks/useFormspree'
import { trackEvent } from '@/lib/posthog'
import { POSTHOG_EVENTS, FORMSPREE_FORM_IDS } from '@/lib/constants'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_RE = /^\d{10}$/

interface FieldErrors {
  name?: string
  email?: string
  phone?: string
  origin?: string
  destination?: string
  pickup_date?: string
}

export function QuoteForm() {
  const { status, error, submit } = useFormspree(
    FORMSPREE_FORM_IDS.QUOTES
  )
  const [fields, setFields] = useState({
    company_name: '',
    mc_number: '',
    name: '',
    email: '',
    phone: '',
    origin: '',
    destination: '',
    load_type: 'Dry Van',
    pickup_date: '',
    tcpaConsent: false,
  })
  const [errors, setErrors] = useState<FieldErrors>({})

  function set(key: string, value: string) {
    setFields(prev => ({ ...prev, [key]: value }))
  }

  function validate(): boolean {
    const errs: FieldErrors = {}
    if (!fields.name.trim()) errs.name = 'Name is required'
    if (!EMAIL_RE.test(fields.email)) errs.email = 'Enter a valid email address'
    if (!PHONE_RE.test(fields.phone.replace(/\D/g, '')))
      errs.phone = 'Enter a 10-digit phone number'
    if (!fields.origin.trim()) errs.origin = 'Origin is required'
    if (!fields.destination.trim()) errs.destination = 'Destination is required'
    if (!fields.pickup_date) errs.pickup_date = 'Pickup date is required'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return
    submit({ ...fields })
    trackEvent(POSTHOG_EVENTS.QUOTE_SUBMIT, { load_type: fields.load_type })
  }

  if (status === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="glass-card rounded-2xl p-10 text-center"
      >
        <p className="text-white text-xl font-semibold">Quote request received!</p>
        <p className="text-[color:var(--foreground-muted)] mt-2 text-sm">
          We&apos;ll respond within 2 hours during business hours.
        </p>
      </motion.div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      aria-label="Quote request form"
      noValidate
      className="glass-card rounded-2xl p-8 flex flex-col gap-5"
    >
      <div aria-live="polite" aria-atomic="true" className="min-h-[1.25rem]">
        {error && <p className="text-red-400 text-sm font-medium">{error}</p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <label htmlFor="q-company_name" className="text-sm font-medium text-[color:var(--foreground-muted)]">
            Company Name
          </label>
          <motion.input
            id="q-company_name"
            type="text"
            className="glass-input rounded-lg px-4 py-3 text-white"
            value={fields.company_name}
            onChange={e => set('company_name', e.target.value)}
            autoComplete="organization"
            placeholder="Brokerage or shipper name"
            whileFocus={{ scale: 1.01, boxShadow: '0 0 0 2px var(--accent)' }}
            transition={{ duration: 0.15 }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="q-mc_number" className="text-sm font-medium text-[color:var(--foreground-muted)]">
            MC Number
          </label>
          <motion.input
            id="q-mc_number"
            type="text"
            className="glass-input rounded-lg px-4 py-3 text-white"
            value={fields.mc_number}
            onChange={e => set('mc_number', e.target.value)}
            placeholder="MC-XXXXXXX"
            whileFocus={{ scale: 1.01, boxShadow: '0 0 0 2px var(--accent)' }}
            transition={{ duration: 0.15 }}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="q-name" className="text-sm font-medium text-[color:var(--foreground-muted)]">
          Your Name <span aria-hidden="true">*</span>
        </label>
        <motion.input
          id="q-name"
          type="text"
          className={`glass-input rounded-lg px-4 py-3 text-white${errors.name ? ' border border-red-500' : ''}`}
          value={fields.name}
          onChange={e => set('name', e.target.value)}
          autoComplete="name"
          whileFocus={{ scale: 1.01, boxShadow: '0 0 0 2px var(--accent)' }}
          transition={{ duration: 0.15 }}
        />
        {errors.name && <p className="text-red-400 text-xs">{errors.name}</p>}
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="q-email" className="text-sm font-medium text-[color:var(--foreground-muted)]">
          Email <span aria-hidden="true">*</span>
        </label>
        <motion.input
          id="q-email"
          type="email"
          className={`glass-input rounded-lg px-4 py-3 text-white${errors.email ? ' border border-red-500' : ''}`}
          value={fields.email}
          onChange={e => set('email', e.target.value)}
          autoComplete="email"
          whileFocus={{ scale: 1.01, boxShadow: '0 0 0 2px var(--accent)' }}
          transition={{ duration: 0.15 }}
        />
        {errors.email && <p className="text-red-400 text-xs">{errors.email}</p>}
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="q-phone" className="text-sm font-medium text-[color:var(--foreground-muted)]">
          Phone <span aria-hidden="true">*</span>
        </label>
        <motion.input
          id="q-phone"
          type="tel"
          className={`glass-input rounded-lg px-4 py-3 text-white${errors.phone ? ' border border-red-500' : ''}`}
          value={fields.phone}
          onChange={e => set('phone', e.target.value)}
          autoComplete="tel"
          placeholder="10-digit number"
          whileFocus={{ scale: 1.01, boxShadow: '0 0 0 2px var(--accent)' }}
          transition={{ duration: 0.15 }}
        />
        {errors.phone && <p className="text-red-400 text-xs">{errors.phone}</p>}
        <p className="text-xs text-gray-500 max-w-prose">
          By providing your phone number, you agree to receive SMS messages from ADG2 Transport Corp for service updates and communication. Message frequency may vary. Message and data rates may apply. Reply STOP to opt out, HELP for assistance.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <label htmlFor="q-origin" className="text-sm font-medium text-[color:var(--foreground-muted)]">
            Origin <span aria-hidden="true">*</span>
          </label>
          <motion.input
            id="q-origin"
            type="text"
            className={`glass-input rounded-lg px-4 py-3 text-white${errors.origin ? ' border border-red-500' : ''}`}
            value={fields.origin}
            onChange={e => set('origin', e.target.value)}
            whileFocus={{ scale: 1.01, boxShadow: '0 0 0 2px var(--accent)' }}
            transition={{ duration: 0.15 }}
          />
          {errors.origin && <p className="text-red-400 text-xs">{errors.origin}</p>}
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="q-destination" className="text-sm font-medium text-[color:var(--foreground-muted)]">
            Destination <span aria-hidden="true">*</span>
          </label>
          <motion.input
            id="q-destination"
            type="text"
            className={`glass-input rounded-lg px-4 py-3 text-white${errors.destination ? ' border border-red-500' : ''}`}
            value={fields.destination}
            onChange={e => set('destination', e.target.value)}
            whileFocus={{ scale: 1.01, boxShadow: '0 0 0 2px var(--accent)' }}
            transition={{ duration: 0.15 }}
          />
          {errors.destination && <p className="text-red-400 text-xs">{errors.destination}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <label htmlFor="q-load_type" className="text-sm font-medium text-[color:var(--foreground-muted)]">
            Load Type
          </label>
          <motion.select
            id="q-load_type"
            className="glass-input rounded-lg px-4 py-3 text-white"
            value={fields.load_type}
            onChange={e => set('load_type', e.target.value)}
            whileFocus={{ scale: 1.01, boxShadow: '0 0 0 2px var(--accent)' }}
            transition={{ duration: 0.15 }}
          >
            <option value="Dry Van">Dry Van</option>
            <option value="Reefer">Reefer</option>
            <option value="Power Only">Power Only</option>
          </motion.select>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="q-pickup_date" className="text-sm font-medium text-[color:var(--foreground-muted)]">
            Pickup Date <span aria-hidden="true">*</span>
          </label>
          <motion.input
            id="q-pickup_date"
            type="date"
            className={`glass-input rounded-lg px-4 py-3 text-white${errors.pickup_date ? ' border border-red-500' : ''}`}
            value={fields.pickup_date}
            onChange={e => set('pickup_date', e.target.value)}
            whileFocus={{ scale: 1.01, boxShadow: '0 0 0 2px var(--accent)' }}
            transition={{ duration: 0.15 }}
          />
          {errors.pickup_date && <p className="text-red-400 text-xs">{errors.pickup_date}</p>}
        </div>
      </div>

      <label className="flex items-start gap-3 text-sm text-[color:var(--foreground-muted)] cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 accent-[color:var(--accent)] flex-shrink-0"
          checked={fields.tcpaConsent}
          onChange={e => setFields(prev => ({ ...prev, tcpaConsent: e.target.checked }))}
          aria-required="true"
        />
        <span>
          I agree to receive transactional SMS messages from ADG2 Transport Corp regarding my request. Message and data rates may apply. Reply STOP to opt out.{' '}
          <a href="/privacy-policy" className="underline hover:text-white transition-colors">Privacy Policy</a>
          {' '}·{' '}
          <a href="/terms-of-service" className="underline hover:text-white transition-colors">Terms</a>
        </span>
      </label>

      <motion.button
        type="submit"
        disabled={status === 'loading' || !fields.tcpaConsent}
        className="glass-button rounded-xl px-8 py-4 font-semibold text-base mt-2 disabled:opacity-50 overflow-hidden"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.97 }}
        transition={{ type: 'spring', stiffness: 400, damping: 17 }}
      >
        <AnimatePresence mode="wait" initial={false}>
          {status === 'loading' ? (
            <motion.span
              key="loading"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.15 }}
              className="block"
            >
              Sending…
            </motion.span>
          ) : (
            <motion.span
              key="idle"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.15 }}
              className="block"
            >
              Request Quote
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    </form>
  )
}
